import { SpokePageSpinner } from '@doltech/core/lib/auth/SpokePageSpinner';
import { encryptData } from '@doltech/core/lib/encrypt-page-data/encryptData';
import { environment } from '@doltech/core/lib/environment/environment';
import { stripFilterAndSearchInUrl } from '@doltech/ui/lib/figma/LandingFilterV2/store/seo-filter.utils';
import {
  fetchPublishedPagesWithFilter,
  fetchRedirectPage,
  getCommonDataPreRendering,
} from '@doltech/ui/lib/figma/Layout/Landing/api/pages-landing.m2m';
import { retainOnlyUrlOfStaticPageLink } from '@doltech/ui/lib/hocs/retainOnlyUrlOfStaticPageLink';
import {
  PageTypeEnum,
  PageTypeForBlogWithoutSample,
  PageTypeForLandingDictionaryPath,
} from '@doltech/utils/lib/constants';
import { printNetworkError } from '@doltech/utils/lib/error';
import { fixRedirectUrl, getCorpWebsiteUrl } from '@doltech/utils/lib/url';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import * as React from 'react';

import { buildLandingSEOInfo } from '@doltech/ui/lib/shared/components/SlugPage/buildLandingSEOInfo';
import { replaceLandingCanonicalUrl } from '@doltech/ui/lib/shared/components/SlugPage/changeLandingCanonicalUrl';
import { CommonSlugPage } from '@doltech/ui/lib/shared/components/SlugPage/CommonSlugPage';
import { filterDataByPageType } from '../routers/filterDataByPageType';
import { getDataPreRendering } from '../routers/route';
import { PageTemplate } from '../routers/route.template';
import { useLandingJsonLdSchemaForTuHocIelts } from '@doltech/ui/lib/hooks/useLandingJsonLdSchemaForTuHocIelts';
//
const SlugPageError = (error) => (
  <div>
    <pre>Response error: {JSON.stringify(error, null, 2)}</pre>
  </div>
);

const FallbackPage = (props) => {
  const { urlInfo, error } = props;
  const { isFallback, reload } = useRouter();

  React.useEffect(() => {
    if (!urlInfo && process.env.NODE_ENV === 'development') {
      reload();
    }
  }, [urlInfo, reload]);

  if (error) {
    return <SlugPageError error={error} />;
  }

  if (isFallback) {
    return (
      <div>
        <h1>Page not found</h1>
        <p>You may need to refresh this page, we need sometime to generate this page...</p>
      </div>
    );
  }

  if (!urlInfo) {
    return <SpokePageSpinner />;
  }

  return <SlugPage {...props} />;
};

export const SlugPage = (props) => {
  const { slug, isStructuralSEO, urlInfo } = props;

  const urlInfoTitle = urlInfo?.thirdParty?.title || urlInfo?.dol?.title;

  const defaultLdSchema = useLandingJsonLdSchemaForTuHocIelts({
    testType: urlInfo?.data?.testType,
    skill: urlInfo?.data?.skill,
    title: urlInfoTitle,
  });

  return (
    <CommonSlugPage
      {...props}
      defaultLdSchema={defaultLdSchema}
      parseLocalSeoMedataOption={{
        replace: replaceLandingCanonicalUrl({ slug, isStructuralSEO }),
      }}
      hardCodeHeadTags={`
        <meta property="og:locale" content="vi_VN" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:alt" content="${urlInfoTitle}" />
        <meta name="twitter:site" content="@EnglishDol" />
        ${
          urlInfo?.type === PageTypeEnum.DAILY_LEARNING_NEWS
            ? '<meta name="robots" content="noindex,nofollow" />'
            : ''
        }
      `}
    >
      {({ allProps }) => <PageTemplate pageType={urlInfo?.type} pageProps={allProps} />}
    </CommonSlugPage>
  );
};

export default FallbackPage;

const getPageMatchRedirectUrl = (pages, url) => {
  return pages.find((page) =>
    page.redirectUrls?.some((item) => item.replace(/\//g, '') === url.replace(/\//g, ''))
  );
};

const redirectPageFromPageTypes = ({ pageTypes, pageType, redirectToDomain, path }) => {
  if (pageTypes.includes(pageType)) {
    const redirectTo = fixRedirectUrl(`${redirectToDomain}/${path}`);
    return {
      redirect: {
        destination: redirectTo,
        statusCode: 301,
      },
    };
  }
};

export const getServerSideProps = async (context) => {
  const { req, params, query } = context;
  const slug = params.slug.join('/').replace(/([^:])(\/\/+)/g, '$1/');
  context.res.setHeader('Cache-Control', 'public, max-age=10, stale-while-revalidate=60');

  // we'll check if any url in system match 100% with requested url
  // if no url matched => check if it's a structural url
  let slugUrl = `/${params.slug.join('/')}`.replace(/([^:])(\/\/+)/g, '$1/');
  let urlInfo;
  let data = {};
  let filterUrls = [];
  let commonData = {};
  let redirectPage;

  Sentry.setTag('slug_url', slugUrl);

  const checkRedirectUrl = await fetchRedirectPage({ url: req.url, app: 'TU_HOC_IELTS' });

  if (checkRedirectUrl?.targetUrl && checkRedirectUrl?.httpCode) {
    const redirectTo = fixRedirectUrl(checkRedirectUrl?.targetUrl);
    const statusCode = Number(checkRedirectUrl?.httpCode);
    return {
      redirect: {
        destination: redirectTo,
        statusCode,
      },
    };
  }

  try {
    filterUrls = await fetchPublishedPagesWithFilter({ url: slugUrl });
    urlInfo = filterUrls.find((item) => item.url.replace(/\//g, '') === slugUrl.replace(/\//g, ''));

    if (urlInfo?.type === 'LANDING_BLOG') {
      return {
        redirect: {
          destination: getCorpWebsiteUrl('/gioi-thieu-linearthinking'),
          statusCode: 302,
        },
      };
    }

    if (PageTypeForLandingDictionaryPath.includes(urlInfo?.type)) {
      return {
        redirect: {
          destination: '/not-found',
          statusCode: 302,
        },
      };
    }

    const redirectToLandingBlog = redirectPageFromPageTypes({
      pageType: urlInfo?.type,
      pageTypes: PageTypeForBlogWithoutSample,
      redirectToDomain: environment.REACT_APP_DOL_CORP_WEBSITE_HOST_NAME,
      path: slugUrl,
    });
    if (redirectToLandingBlog) {
      return redirectToLandingBlog;
    }

    redirectPage = getPageMatchRedirectUrl(filterUrls, slugUrl);
    if (redirectPage) {
      const redirectTo = fixRedirectUrl(redirectPage.url);
      return {
        redirect: {
          destination: redirectTo,
          statusCode: 301,
        },
      };
    }
  } catch (ex) {
    if (ex.request || ex.response) {
      printNetworkError(ex, 'compact');
      Sentry.setExtra('error', ex);
      Sentry.captureException(ex);
      Sentry.flush(2000);
    } else {
      console.error('[Error] getStaticProps error:', ex);
      Sentry.setExtra('error', ex);
      Sentry.captureException(ex);
      Sentry.flush(2000);
    }
  }

  if (!urlInfo) {
    try {
      slugUrl = stripFilterAndSearchInUrl({ url: slugUrl });
      Sentry.setTag('slug_url', slugUrl);
      filterUrls = await fetchPublishedPagesWithFilter({ url: slugUrl });
      urlInfo = filterUrls.find(
        (item) => item.url.replace(/\//g, '') === slugUrl.replace(/\//g, '')
      );
      redirectPage = getPageMatchRedirectUrl(filterUrls, slugUrl);
      if (redirectPage) {
        console.info('redirect to');
        return {
          redirect: {
            destination: redirectPage.url,
            statusCode: 301,
          },
        };
      }
    } catch (ex) {
      if (ex.request || ex.response) {
        printNetworkError(ex, 'compact');
        Sentry.setExtra('error', ex);
        Sentry.captureException(ex);
        Sentry.flush(2000);
      } else {
        console.error('[Error] getStaticProps error:', ex);
        Sentry.setExtra('error', ex);
        Sentry.captureException(ex);
        Sentry.flush(2000);
      }
    }
  }

  try {
    data = filterDataByPageType(await getDataPreRendering(urlInfo, query), urlInfo?.type);

    commonData = (await retainOnlyUrlOfStaticPageLink(await getCommonDataPreRendering())) as any;
  } catch (ex) {
    if (ex.request || ex.response) {
      printNetworkError(ex, 'compact');
      Sentry.setExtra('error', ex);
      Sentry.captureException(ex);
      Sentry.flush(2000);
    } else {
      console.error('[Error] getDataPreRendering error:', ex);
      Sentry.setExtra('error', ex);
      Sentry.captureException(ex);
      Sentry.flush(2000);
    }
  }

  const notFound = Boolean(urlInfo) === false;

  if (notFound) {
    return {
      redirect: {
        destination: '/not-found',
        statusCode: 302,
      },
    };
  }

  const { structuralSeo, schemaScriptString, isStructuralSEO } = buildLandingSEOInfo({
    slug,
    urlInfo,
  });

  return {
    props: encryptData({
      key: slugUrl,
      urlInfo,
      structuralSeo,
      schemaScriptString,
      isStructuralSEO,
      data: {
        ...commonData,
        ...data,
      },
      slug,
    }),
  };
};
